import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { CurrencyDollarIcon, PhotoIcon, ArrowUpTrayIcon, StarIcon, CheckCircleIcon, XCircleIcon, ShieldCheckIcon, DocumentTextIcon, PhoneIcon } from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PayRent = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Check if user is verified
  const isVerified = user?.isVerified || user?.verificationStatus === 'verified';
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rewardBalance, setRewardBalance] = useState(0);
  const [ocrResult, setOcrResult] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);

  useEffect(() => {
    fetchRewardBalance();
    fetchMonthlySummary();
  }, [user]);

  const fetchRewardBalance = async () => {
    try {
      const response = await axios.get(`${API_URL}/rewards/balance`);
      setRewardBalance(response.data.currentBalance);
    } catch (error) {
      console.error('Error fetching reward balance:', error);
    }
  };

  const fetchMonthlySummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/rent/monthly-summary`);
      setMonthlySummary(response.data);
    } catch (error) {
      console.error('Error fetching monthly summary:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload an image (JPEG, PNG, GIF) or PDF.');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size too large. Please upload a file smaller than 10MB.');
      return;
    }

    setSelectedFile(file);
    setError('');
    setOcrResult(null);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a payment image to upload');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('paymentImage', selectedFile);

      const response = await axios.post(
        `${API_URL}/rent/upload-payment`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setOcrResult(response.data.ocrResult);
      
      if (response.data.rentPayment.status === 'completed') {
        setSuccess(
          `Payment verified! You earned ${Math.round(response.data.rentPayment.rewardEarned)} LOCU points.`
        );
        // Reset form
        setSelectedFile(null);
        setPreview(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Refresh data
        fetchRewardBalance();
        fetchMonthlySummary();
        if (refreshUser) {
          refreshUser();
        }
        // Navigate after delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        setSuccess(
          'Payment uploaded successfully! It is pending verification. You will receive your rewards once verified.'
        );
        // Reset form
        setSelectedFile(null);
        setPreview(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        fetchMonthlySummary();
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(
        error.response?.data?.message || 
        'Failed to upload payment. Please try again with a clearer image.'
      );
    } finally {
      setLoading(false);
    }
  };

  // If user is not verified, show verification gate
  if (!isVerified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 py-8">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShieldCheckIcon className="h-10 w-10 text-primary-600" />
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Verify Your Rental First
            </h1>
            <p className="text-gray-600 text-lg mb-8">
              To start earning 2% rewards on rent payments, we need to verify your rental details. Choose one of the options below:
            </p>

            <div className="space-y-4">
              {/* Option 1: Upload Agreement */}
              <button
                onClick={() => navigate('/verification')}
                className="w-full flex items-center justify-between p-6 bg-gradient-to-r from-primary-50 to-primary-100 border-2 border-primary-200 rounded-xl hover:border-primary-400 hover:shadow-lg transition-all group"
              >
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mr-4">
                    <DocumentTextIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-bold text-gray-900">Upload Rent Agreement</h3>
                    <p className="text-sm text-gray-600">Instant verification with your rental document</p>
                  </div>
                </div>
                <span className="text-primary-600 font-semibold group-hover:translate-x-1 transition-transform">→</span>
              </button>

              {/* Option 2: Landlord Contact */}
              <button
                onClick={() => navigate('/verification', { state: { tab: 'landlord' } })}
                className="w-full flex items-center justify-between p-6 bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-xl hover:border-blue-400 hover:shadow-lg transition-all group"
              >
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mr-4">
                    <PhoneIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-bold text-gray-900">Provide Landlord Contact</h3>
                    <p className="text-sm text-gray-600">We'll verify with your landlord directly</p>
                  </div>
                </div>
                <span className="text-blue-600 font-semibold group-hover:translate-x-1 transition-transform">→</span>
              </button>
            </div>

            <div className="mt-8 p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-600">
                <strong>Why verify?</strong> Verification ensures rewards go to genuine renters and helps us build your rent credit profile.
              </p>
            </div>

            <button
              onClick={() => navigate('/dashboard')}
              className="mt-6 text-gray-500 hover:text-gray-700 text-sm"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-500 to-primary-700 bg-clip-text text-transparent mb-2">
            Pay Rent & Earn Points
          </h1>
          <p className="text-gray-600 text-lg">Upload your rent payment screenshot and earn 2% LOCU points instantly!</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Upload Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              {/* Points Card - Enhanced */}
              <div className="mb-8 relative bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 rounded-2xl p-6 text-white overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
                <div className="relative flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/80 mb-1">Your LOCU Points Balance</p>
                    <p className="text-4xl font-bold mb-2">{Math.round(rewardBalance).toLocaleString('en-IN')}</p>
                    <p className="text-sm text-white/90">Earn 2% on every rent payment</p>
                  </div>
                  <div className="hidden sm:block">
                    <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                      <StarIcon className="h-12 w-12 text-white" />
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="mb-6 bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-xl flex items-center">
                  <XCircleIcon className="h-6 w-6 mr-3 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {success && (
                <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 text-green-700 px-6 py-4 rounded-xl flex items-center">
                  <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0" />
                  <span>{success}</span>
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <PhotoIcon className="h-6 w-6 mr-2 text-primary-600" />
                    Upload Rental Payment Screenshot
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-8 pb-8 border-3 border-dashed border-gray-300 rounded-2xl hover:border-primary-400 hover:bg-primary-50/50 transition-all cursor-pointer group">
                    <div className="space-y-1 text-center">
                      {preview ? (
                        <div className="space-y-2">
                          <img
                            src={preview}
                            alt="Preview"
                            className="mx-auto h-48 object-contain rounded-lg"
                          />
                          <p className="text-xs text-gray-500">{selectedFile?.name}</p>
                        </div>
                      ) : (
                        <>
                          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                            <PhotoIcon className="h-10 w-10 text-primary-600" />
                          </div>
                          <div className="flex flex-col items-center text-sm text-gray-600">
                            <label
                              htmlFor="file-upload"
                              className="relative cursor-pointer px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105 mb-2"
                            >
                              <span className="flex items-center">
                                <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                                Upload Receipt
                              </span>
                              <input
                                id="file-upload"
                                name="file-upload"
                                type="file"
                                className="sr-only"
                                accept="image/*,application/pdf"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                              />
                            </label>
                            <p className="text-gray-500">or drag and drop here</p>
                            <p className="text-xs text-gray-400 mt-2">PNG, JPG, GIF, PDF up to 10MB</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {selectedFile && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFile(null);
                        setPreview(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      className="mt-2 text-sm text-red-600 hover:text-red-700"
                    >
                      Remove file
                    </button>
                  )}
                </div>

                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || loading}
                  className="w-full bg-gradient-to-r from-primary-500 to-primary-700 text-white py-4 px-6 rounded-xl font-bold text-lg hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-primary-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transform hover:scale-105 transition-all"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-3 border-white border-t-transparent mr-3"></div>
                      Processing & Verifying...
                    </>
                  ) : (
                    <>
                      <StarIcon className="h-6 w-6 mr-2" />
                      Upload & Verify Payment
                    </>
                  )}
                </button>

                {ocrResult && (
                  <div className="mt-6 p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl border-2 border-blue-200">
                    <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center">
                      <StarIcon className="h-5 w-5 mr-2" />
                      OCR Extraction Results
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {ocrResult.extractedData.amount && (
                        <div className="bg-white rounded-xl p-4 border border-primary-100">
                          <p className="text-xs text-primary-600 mb-1">Amount</p>
                          <p className="text-xl font-bold text-gray-900">₹{ocrResult.extractedData.amount.toLocaleString('en-IN')}</p>
                        </div>
                      )}
                      {ocrResult.extractedData.paymentDate && (
                        <div className="bg-white rounded-xl p-4 border border-blue-100">
                          <p className="text-xs text-blue-600 mb-1">Date</p>
                          <p className="text-xl font-bold text-gray-900">
                            {new Date(ocrResult.extractedData.paymentDate).toLocaleDateString('en-IN')}
                          </p>
                        </div>
                      )}
                      {ocrResult.extractedData.landlordName && (
                        <div className="bg-white rounded-xl p-4 border border-primary-100 md:col-span-2">
                          <p className="text-xs text-primary-600 mb-1">Landlord</p>
                          <p className="text-lg font-semibold text-gray-900">{ocrResult.extractedData.landlordName}</p>
                        </div>
                      )}
                    </div>
                    {ocrResult.verification && (
                      <div className="mt-4 pt-4 border-t-2 border-blue-200">
                        <p className="font-bold text-blue-900 mb-3">Verification Status</p>
                        <div className="grid grid-cols-3 gap-3">
                          <div className={`p-3 rounded-xl ${ocrResult.verification.amountMatch ? 'bg-green-100 border-2 border-green-300' : 'bg-red-100 border-2 border-red-300'}`}>
                            <div className="flex items-center justify-center mb-2">
                              {ocrResult.verification.amountMatch ? (
                                <CheckCircleIcon className="h-6 w-6 text-green-600" />
                              ) : (
                                <XCircleIcon className="h-6 w-6 text-red-600" />
                              )}
                            </div>
                            <p className="text-xs font-semibold text-center">{ocrResult.verification.amountMatch ? 'Amount ✓' : 'Amount ✗'}</p>
                          </div>
                          <div className={`p-3 rounded-xl ${ocrResult.verification.landlordMatch ? 'bg-primary-100 border-2 border-primary-300' : 'bg-red-100 border-2 border-red-300'}`}>
                            <div className="flex items-center justify-center mb-2">
                              {ocrResult.verification.landlordMatch ? (
                                <CheckCircleIcon className="h-6 w-6 text-primary-600" />
                              ) : (
                                <XCircleIcon className="h-6 w-6 text-red-600" />
                              )}
                            </div>
                            <p className="text-xs font-semibold text-center">{ocrResult.verification.landlordMatch ? 'Landlord ✓' : 'Landlord ✗'}</p>
                          </div>
                          <div className={`p-3 rounded-xl ${ocrResult.verification.dateValid ? 'bg-green-100 border-2 border-green-300' : 'bg-red-100 border-2 border-red-300'}`}>
                            <div className="flex items-center justify-center mb-2">
                              {ocrResult.verification.dateValid ? (
                                <CheckCircleIcon className="h-6 w-6 text-green-600" />
                              ) : (
                                <XCircleIcon className="h-6 w-6 text-red-600" />
                              )}
                            </div>
                            <p className="text-xs font-semibold text-center">{ocrResult.verification.dateValid ? 'Date ✓' : 'Date ✗'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-6 p-6 bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl border-2 border-primary-200">
                  <p className="text-sm text-primary-900 leading-relaxed">
                    <strong className="text-primary-700">✨ How it works:</strong> Upload a clear photo or screenshot of your rent payment (UPI, bank transfer, etc.). 
                    Our AI extracts the amount and you earn <strong className="text-primary-700">2% LOCU points instantly!</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 sticky top-24">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <CurrencyDollarIcon className="h-6 w-6 mr-2 text-primary-600" />
                Payment History
              </h2>
              {monthlySummary && monthlySummary.summary && monthlySummary.summary.length > 0 ? (
                <div className="space-y-3">
                  {monthlySummary.summary.slice(0, 6).map((month, index) => (
                    <div 
                      key={index} 
                      className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200 hover:shadow-md transition-all hover:border-primary-300"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold text-gray-700">{month.month}</p>
                        <span className="text-xs font-bold px-2 py-1 bg-primary-100 text-primary-700 rounded-full">
                          {month.paymentCount}x
                        </span>
                      </div>
                      <p className="text-2xl font-bold bg-gradient-to-r from-primary-500 to-primary-700 bg-clip-text text-transparent">
                        ₹{month.totalAmount.toLocaleString('en-IN')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{month.paymentCount} payment{month.paymentCount !== 1 ? 's' : ''}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CurrencyDollarIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500 mb-2">No payments yet</p>
                  <p className="text-xs text-gray-400">Upload your first payment screenshot!</p>
                </div>
              )}
              <button
                onClick={() => navigate('/dashboard')}
                className="mt-6 w-full py-3 px-4 bg-gradient-to-r from-primary-500 to-primary-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105"
              >
                View Full History →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayRent;
